import { ApiError } from '@/lib/api'

import type { AssistantCapabilityError, AssistantProposalOutcome } from '../../api/assistant-api'

export type AssistantMessageRole = 'user' | 'assistant' | 'notice'
export type ResolvedOutcome = Exclude<AssistantProposalOutcome, 'pending'>

export const outcomeText: Record<ResolvedOutcome, string> = {
  executing: 'This proposal may have completed. Check before asking again.',
  executed: 'Approved. The proposal completed.',
  rejected: 'Proposal rejected.',
  expired: 'This proposal expired before it was approved.',
  stale: 'Something changed since that proposal was made. Ask again.',
  forbidden: 'You no longer have permission to approve this proposal.',
  failed: 'That proposal failed to run.',
}

export function messageClassName(role: AssistantMessageRole) {
  if (role === 'user') return 'ml-8 min-w-0 break-words rounded-md bg-primary p-2 text-sm text-primary-foreground'
  if (role === 'notice') return 'text-muted-foreground break-words text-sm italic'
  return 'mr-8 min-w-0 break-words rounded-md bg-muted p-2 text-sm'
}

export function messageLabel(role: AssistantMessageRole) {
  if (role === 'user') return 'You: '
  if (role === 'notice') return 'Notice: '
  return 'Assistant: '
}

export function capabilityErrorText(failure: AssistantCapabilityError) {
  return `Couldn\u2019t complete the \u201c${failure.tool.replaceAll('_', ' ')}\u201d step. ${failure.reason}`
}

export function streamErrorText(code: string) {
  switch (code) {
    case 'AI_REQUEST_TIMEOUT':
      return 'The assistant took too long to respond. Try again.'
    case 'AI_UNAVAILABLE':
      return 'The assistant is unavailable right now. Try again later.'
    case 'AI_TURN_STOPPED':
    case 'AI_STREAM_ABANDONED':
      return 'Stopped before the reply finished.'
    case 'TURN_IN_PROGRESS':
      return 'That message is still being handled. Give it a moment.'
    case 'FORBIDDEN':
      return 'You do not have permission for that request.'
    case 'AI_NOT_CONFIGURED':
      return 'The assistant is not available in this environment.'
    case 'AI_NO_BILLING_ACCOUNT':
      return 'The assistant is off for you because your access isn’t under a billing account. Ask an admin to set one up.'
    case 'AI_MULTIPLE_BILLING_ACCOUNTS':
      return 'The assistant is off for you because your access spans more than one billing account. Ask an admin to narrow your access.'
    case 'ASSISTANT_TIER_NOT_ENABLED':
      return 'That tier is no longer available here, so the picker is back on the default tier.'
    default:
      return 'The assistant could not complete that request.'
  }
}

export function outcomeFromFailure(failure: unknown): ResolvedOutcome | null {
  if (failure instanceof ApiError) {
    if (failure.status === 403) return 'forbidden'
    if (failure.code === 'PROPOSAL_UNAVAILABLE') return 'expired'
    if (failure.code === 'PROPOSAL_STALE') return 'stale'
  }
  return null
}
